import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import logo from "@/assets/thevibingco_logo.svg.asset.json";

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: z.object({
    token: z.string().optional(),
  }),
  head: () => ({
    meta: [
      { title: "Unsubscribe — the Vibing Company" },
      { name: "description", content: "Unsubscribe from emails from the Vibing Company." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UnsubscribePage,
});

type State = "loading" | "valid" | "already" | "invalid" | "success" | "error";

function UnsubscribePage() {
  const token = Route.useSearch({ select: (s) => s.token as string | undefined });
  const [state, setState] = useState<State>("loading");

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    let cancelled = false;
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.valid) {
          setState("valid");
        } else if (res.ok && data.reason === "already_unsubscribed") {
          setState("already");
        } else {
          setState("invalid");
        }
      })
      .catch(() => {
        if (!cancelled) setState("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    setState("loading");
    try {
      const res = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setState("success");
      } else if (data.reason === "already_unsubscribed") {
        setState("already");
      } else {
        setState("error");
      }
    } catch {
      setState("error");
    }
  };

  return (
    <div className="flex min-h-svh items-center justify-center bg-[#1e2023] px-4 py-12">
      <div className="w-full max-w-md rounded-2xl bg-[#1e2023] p-8 text-[#fafbff] shadow-2xl sm:p-10">
        <div className="mb-8 flex items-center gap-3">
          <img src={logo.url} alt="the Vibing Company" className="h-7 w-auto" />
          <span className="text-sm font-medium tracking-tight text-[#a8acb3]">
            the Vibing Company
          </span>
        </div>

        {state === "loading" && (
          <div className="flex flex-col items-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#a8acb3] border-t-[#fafbff]" />
            <p className="mt-4 text-sm text-[#a8acb3]">Verifying your request…</p>
          </div>
        )}

        {state === "valid" && (
          <div>
            <h1 className="text-2xl font-medium tracking-tight">Unsubscribe</h1>
            <p className="mt-3 text-sm leading-relaxed text-[#d4d6da]">
              You're about to unsubscribe from emails from the Vibing Company. You'll no
              longer receive proposal updates, payment receipts, or project notifications.
            </p>
            <button
              onClick={handleConfirm}
              className="mt-6 w-full rounded-full bg-[#fafbff] px-6 py-3 text-sm font-semibold text-[#1e2023] transition-colors hover:bg-white"
            >
              Confirm unsubscribe
            </button>
          </div>
        )}

        {state === "success" && (
          <div>
            <h1 className="text-2xl font-medium tracking-tight">You're unsubscribed</h1>
            <p className="mt-3 text-sm leading-relaxed text-[#d4d6da]">
              You won't receive further emails from us. If this was a mistake, you can
              resubscribe by contacting us directly.
            </p>
          </div>
        )}

        {state === "already" && (
          <div>
            <h1 className="text-2xl font-medium tracking-tight">Already unsubscribed</h1>
            <p className="mt-3 text-sm leading-relaxed text-[#d4d6da]">
              This email address is already on our unsubscribe list. You won't receive
              further emails from us.
            </p>
          </div>
        )}

        {(state === "invalid" || state === "error") && (
          <div>
            <h1 className="text-2xl font-medium tracking-tight">Something went wrong</h1>
            <p className="mt-3 text-sm leading-relaxed text-[#d4d6da]">
              {state === "invalid"
                ? "This unsubscribe link is invalid or has expired. If you believe this is an error, please contact us directly."
                : "We couldn't process your unsubscribe request. Please try again or contact us directly."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
